import { useEffect, useMemo, useRef, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { Dialog, DialogPanel } from '@headlessui/react';
import { OptimalAILogo } from './OptimalAILogo';
import { MarkdownContent } from './MarkdownContent';
import { mockClinicianName, mockPatients } from '@/data/mock';
import { postChat, type ApiChatMessage } from '@/lib/chat';
import type { Patient } from '@/lib/types';

interface AIAssistantDrawerProps {
  open: boolean;
  onClose: () => void;
}

interface ChatMessage {
  id: number;
  role: 'user' | 'ai';
  content: string;
}

const COHORT_SUGGESTIONS = [
  'Resume los pacientes prioritarios de hoy',
  '¿Quién no tiene seguimiento en 6 meses?',
  'Redacta un recordatorio para los pacientes inactivos',
  'Tendencias de Optimal Score este trimestre',
];

interface PatientAction {
  icon: 'profile' | 'biomarkers' | 'evolution' | 'plan';
  title: string;
  hint: string;
  prompt: (p: Patient) => string;
}

const PATIENT_ACTIONS: PatientAction[] = [
  {
    icon: 'profile',
    title: 'Perfil clínico',
    hint: 'Resumen del estado actual',
    prompt: (p) => `Resume el perfil clínico de ${firstNameOf(p.name)}`,
  },
  {
    icon: 'biomarkers',
    title: 'Biomarcadores',
    hint: '¿Qué requiere atención?',
    prompt: () => '¿Qué biomarcadores requieren atención prioritaria?',
  },
  {
    icon: 'evolution',
    title: 'Evolución',
    hint: 'Compara vs eval. anterior',
    prompt: (p) => `Compara con la última evaluación de ${firstNameOf(p.name)}`,
  },
  {
    icon: 'plan',
    title: 'Plan de seguimiento',
    hint: 'Próximos pasos sugeridos',
    prompt: () => 'Genera un plan de seguimiento personalizado',
  },
];

function firstName(full: string): string {
  // "Dr. Andrés Vásquez" → "Andrés"
  const parts = full.replace(/^Dr\.?\s+/i, '').split(/\s+/);
  return parts[0] || full;
}

function firstNameOf(name: string): string {
  return name.split(/\s+/)[0] || name;
}

function toApiHistory(messages: ChatMessage[]): ApiChatMessage[] {
  return messages.map((m) => ({
    role: m.role === 'ai' ? 'assistant' : 'user',
    content: m.content,
  }));
}

export function AIAssistantDrawer({ open, onClose }: AIAssistantDrawerProps) {
  const [showFlash, setShowFlash] = useState(true);
  const [prompt, setPrompt] = useState('');
  const [thinking, setThinking] = useState(false);
  const [bursting, setBursting] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messageIdRef = useRef(0);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const patientMatch = useMatch('/patients/:id');
  const reportMatch = useMatch('/patients/:id/report');
  const patientId = patientMatch?.params.id ?? reportMatch?.params.id ?? null;
  const patient = useMemo<Patient | null>(
    () => (patientId ? mockPatients.find((p) => p.id === patientId) ?? null : null),
    [patientId]
  );

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 220)}px`;
  }, [prompt]);

  useEffect(() => {
    if (!open) {
      setShowFlash(true);
      setPrompt('');
      setThinking(false);
      setBursting(false);
      setMessages([]);
      setError(null);
      messageIdRef.current = 0;
      return;
    }
    setShowFlash(true);
    const t = window.setTimeout(() => setShowFlash(false), 2500);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, thinking, error]);

  const handleAsk = (text?: string) => {
    const value = (text ?? prompt).trim();
    if (!value || thinking) return;

    setError(null);
    setBursting(true);
    setPrompt('');

    window.setTimeout(() => {
      setBursting(false);
      const userMsg: ChatMessage = {
        id: ++messageIdRef.current,
        role: 'user',
        content: value,
      };
      const nextMessages = [...messages, userMsg];
      setMessages(nextMessages);
      setThinking(true);

      postChat(toApiHistory(nextMessages), patient?.id ?? null)
        .then((content) => {
          const aiMsg: ChatMessage = {
            id: ++messageIdRef.current,
            role: 'ai',
            content,
          };
          setMessages((prev) => [...prev, aiMsg]);
        })
        .catch((e: Error) => {
          setError(e.message || 'No pude conectar con el modelo. Reintenta en un momento.');
        })
        .finally(() => {
          setThinking(false);
        });
    }, 650);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  const hasConversation = messages.length > 0 || thinking || !!error;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="ai-drawer-fade-slow fixed inset-0 z-[100] bg-black/30 dark:bg-black/55 flex justify-end p-3.5 max-[760px]:p-2"
    >
      <DialogPanel
        className={`ai-drawer-slide-slow ai-edge relative w-full max-w-[440px] h-full ai-bg backdrop-blur-xl backdrop-saturate-150 rounded-2xl flex flex-col overflow-hidden ${bursting ? 'ai-launching' : ''}`}
      >
        <div aria-hidden="true" className="ai-glow absolute inset-0 pointer-events-none" />
        <div aria-hidden="true" className="ai-grid absolute inset-0 pointer-events-none" />
        {bursting && (
          <div aria-hidden="true" className="ai-burst absolute inset-0 pointer-events-none z-40" />
        )}
        {showFlash && (
          <div
            aria-hidden="true"
            className="absolute inset-0 grid place-items-center pointer-events-none z-50"
          >
            <span className="ai-flash-text text-primary">
              <OptimalAILogo width={180} />
            </span>
          </div>
        )}
        {open && (
          <div
            key={open ? 'open' : 'closed'}
            className="ai-drawer-content flex flex-col flex-1 min-h-0"
          >
            <header className="flex items-start justify-between gap-3 px-[22px] pt-5 pb-3 shrink-0">
              <div className="flex flex-col gap-1.5 min-w-0">
                <OptimalAILogo width={88} className="text-primary" />
                {patient && (
                  <p className="text-[11px] text-tertiary tracking-[0.02em] m-0 truncate">
                    Asistencia sobre · <span className="text-primary font-medium">{patient.name}</span>
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="text-[13px] w-7 h-7 border border-line-glass bg-transparent text-tertiary rounded-sm cursor-pointer transition-colors hover:ai-bg hover:text-primary shrink-0"
              >
                ✕
              </button>
            </header>

            <div className="flex-1 overflow-y-auto overflow-x-hidden px-[22px] pb-3 flex flex-col gap-3 min-h-0 min-w-0">
              {!hasConversation && (
                <div className="flex-1 flex flex-col justify-center gap-6 pb-6 max-[760px]:gap-5">
                  {patient ? (
                    <PatientIntro patient={patient} onAsk={(t) => handleAsk(t)} />
                  ) : (
                    <CohortIntro
                      clinicianFirstName={firstName(mockClinicianName)}
                      onAsk={(t) => handleAsk(t)}
                    />
                  )}
                </div>
              )}

              {messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}

              {thinking && <ThinkingBubble />}

              {error && <ErrorBubble message={error} />}

              <div ref={messagesEndRef} />
            </div>

            <footer className="px-[18px] pb-4 pt-3 border-t border-line-glass ai-bg backdrop-blur-md shrink-0">
              <div
                className={`flex items-end gap-1.5 pl-3.5 pr-1.5 py-1.5 rounded-3xl border border-line-glass ai-bg backdrop-blur-md transition-all focus-within:border-line-default ${thinking ? 'ai-loading-pulse' : ''}`}
              >
                <textarea
                  ref={textareaRef}
                  rows={1}
                  placeholder="Pregunta algo a Optimal AI…"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={thinking}
                  className="font-inherit text-[13.5px] text-primary bg-transparent border-0 outline-none resize-none flex-1 min-w-0 leading-[1.45] py-1.5 max-h-[220px] overflow-y-auto disabled:opacity-70 placeholder:text-faint break-words"
                  style={{ overflowWrap: 'break-word' }}
                />
                <button
                  type="button"
                  disabled={!prompt.trim() || thinking}
                  onClick={() => handleAsk()}
                  aria-label="Enviar"
                  className="w-8 h-8 rounded-full bg-primary text-surface grid place-items-center cursor-pointer transition-all hover:opacity-90 hover:scale-105 active:scale-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 shrink-0"
                >
                  <ArrowUpIcon />
                </button>
              </div>
            </footer>
          </div>
        )}
      </DialogPanel>
    </Dialog>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <div
      className={`ai-msg-appear flex min-w-0 ${isUser ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[85%] text-[13px] leading-[1.55] rounded-md px-3.5 py-2.5 break-words hyphens-auto ${
          isUser
            ? 'bg-primary text-surface whitespace-pre-line'
            : 'ai-bg backdrop-blur-md border border-line-glass text-primary'
        }`}
        style={{ overflowWrap: 'break-word' }}
      >
        {isUser ? message.content : <MarkdownContent>{message.content}</MarkdownContent>}
      </div>
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div className="ai-msg-appear flex justify-start">
      <div className="ai-bg backdrop-blur-md border border-line-glass rounded-md px-3.5 py-3 max-w-[85%] min-w-[220px] flex flex-col gap-2">
        <div className="ai-spark-line" style={{ width: '92%' }} />
        <div className="ai-spark-line" style={{ width: '74%', animationDelay: '180ms' }} />
        <div className="ai-spark-line" style={{ width: '58%', animationDelay: '360ms' }} />
      </div>
    </div>
  );
}

function ErrorBubble({ message }: { message: string }) {
  return (
    <div className="ai-msg-appear flex justify-start">
      <div
        className="max-w-[85%] text-[13px] leading-[1.55] rounded-md px-3.5 py-2.5 whitespace-pre-line break-words border bg-crit-tint text-crit-deep border-crit"
        style={{ overflowWrap: 'break-word' }}
      >
        {message}
      </div>
    </div>
  );
}

function ArrowUpIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M8 13V3M3.5 7.5 8 3l4.5 4.5" />
    </svg>
  );
}

interface CohortIntroProps {
  clinicianFirstName: string;
  onAsk: (text: string) => void;
}

function CohortIntro({ clinicianFirstName, onAsk }: CohortIntroProps) {
  return (
    <div className="flex flex-col gap-6 max-w-[380px] mx-auto w-full">
      <h2 className="text-[26px] font-medium tracking-[-0.02em] m-0 leading-[1.2] text-primary text-center max-[760px]:text-[22px]">
        ¿En qué puedo ayudarte,{' '}
        <span className="text-tertiary">{clinicianFirstName}?</span>
      </h2>
      <div className="flex flex-col gap-2">
        {COHORT_SUGGESTIONS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onAsk(s)}
            className="text-center text-[12.5px] text-tertiary border border-line-glass bg-transparent rounded-full px-4 py-2 cursor-pointer transition-all hover:bg-hover hover:text-primary hover:border-line-default"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

interface PatientIntroProps {
  patient: Patient;
  onAsk: (text: string) => void;
}

function PatientIntro({ patient, onAsk }: PatientIntroProps) {
  const fn = firstNameOf(patient.name);
  const scoreTone =
    patient.status === 'crit'
      ? 'text-crit-deep'
      : patient.status === 'warn'
        ? 'text-warn-deep'
        : 'text-ok-deep';

  return (
    <div className="flex flex-col gap-5 max-w-[400px] mx-auto w-full">
      <header className="flex flex-col items-center gap-2 text-center px-2">
        <h2 className="text-[24px] font-medium tracking-[-0.02em] m-0 leading-[1.2] text-primary max-[760px]:text-[20px]">
          Conoce a <span className="text-tertiary">{fn}</span> en profundidad
        </h2>
        <p className="text-[12.5px] text-tertiary leading-[1.5] m-0">
          Análisis profundo, navegación de su historia clínica y asistencia en
          tiempo real durante la consulta.
        </p>
      </header>

      <div
        className="grid grid-cols-3 gap-1.5 px-1"
        aria-label="Contexto cargado"
      >
        <ContextChip label="Score" value={patient.optimalScore.toString()} valueClass={scoreTone} />
        <ContextChip
          label="Edad bio."
          value={patient.bioAge.toFixed(1)}
        />
        <ContextChip
          label="Hallazgos"
          value={patient.findings.toString()}
          valueClass={patient.findings > 0 ? 'text-crit-deep' : ''}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        {PATIENT_ACTIONS.map((a, idx) => (
          <button
            key={a.title}
            type="button"
            onClick={() => onAsk(a.prompt(patient))}
            style={{ animationDelay: `${idx * 60}ms` }}
            className="ai-action-card group flex flex-col items-start gap-2 text-left p-3.5 rounded-xl border border-line-glass ai-bg backdrop-blur-2xl backdrop-saturate-150 cursor-pointer transition-all hover:border-line-default hover:-translate-y-0.5 ai-msg-appear"
          >
            <span className="grid place-items-center w-8 h-8 rounded-md ai-bg border border-line-glass text-primary transition-colors group-hover:border-line-default">
              <ActionIcon icon={a.icon} />
            </span>
            <span className="text-[13px] font-semibold tracking-[-0.005em] text-primary leading-[1.3]">
              {a.title}
            </span>
            <span className="text-[11.5px] text-tertiary leading-[1.4]">
              {a.hint}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

interface ContextChipProps {
  label: string;
  value: string;
  valueClass?: string;
}

function ContextChip({ label, value, valueClass }: ContextChipProps) {
  return (
    <div className="flex flex-col gap-0.5 items-center px-2 py-2 rounded-md border border-line-glass ai-bg backdrop-blur-2xl backdrop-saturate-150">
      <span className="text-[10px] font-semibold tracking-[0.06em] uppercase text-muted">
        {label}
      </span>
      <span className={`text-[16px] font-bold tracking-[-0.018em] tnum leading-none ${valueClass ?? 'text-primary'}`}>
        {value}
      </span>
    </div>
  );
}

function ActionIcon({ icon }: { icon: PatientAction['icon'] }) {
  const sw = 1.6;
  switch (icon) {
    case 'profile':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20c.8-3.5 3.6-5.5 7-5.5s6.2 2 7 5.5" />
        </svg>
      );
    case 'biomarkers':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 12h3l2-6 4 12 2-8 2 4h5" />
        </svg>
      );
    case 'evolution':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 17l6-6 4 4 8-8" />
          <path d="M14 7h7v7" />
        </svg>
      );
    case 'plan':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="4" y="5" width="16" height="16" rx="2" />
          <path d="M8 3v4M16 3v4M4 11h16" />
        </svg>
      );
  }
}
